// Single source of truth for auth + role checks.
// Used by the router guard, sidebar, header and every component that needs to
// know whether the current user may modify data.
export function isLoggedIn() {
  return !!localStorage.getItem("user-info");
}

// Roles of the logged-in user (lower-cased), or [] when logged out.
export function getRoles() {
  try {
    const info = JSON.parse(localStorage.getItem("user-info"));
    const roles = (info && info.roles) || [];
    return roles.map((r) => String(r).toLowerCase());
  } catch (e) {
    return [];
  }
}

// admin / superadmin => full access. Everything else is view-only.
export function isAdmin() {
  const roles = getRoles();
  return roles.includes("admin") || roles.includes("superadmin");
}

// A logged-in non-admin is a "viewer": can see everything except the Company
// Knowledge Base and Admin Panel, but cannot add / update / delete anything.
export function isViewOnly() {
  return isLoggedIn() && !isAdmin();
}

// ── Silent access-token renewal ─────────────────────────────────────────────
// When the short-lived access token expires the server answers 401. Instead of
// forcing a re-login we exchange the stored refresh token for a fresh access
// token (the backend rotates the refresh token too). The user never notices.

const OAUTH_CLIENT_ID = 'bid-ui-spa';
const API_URL = process.env.VUE_APP_API_URL;

// Reads the stored session object, or null.
export function getUserInfo() {
  try {
    return JSON.parse(localStorage.getItem('user-info'));
  } catch (e) {
    return null;
  }
}

export function getAccessToken() {
  const info = getUserInfo();
  return info ? info.access_token : null;
}

export function getRefreshToken() {
  const info = getUserInfo();
  return info ? info.refresh_token : null;
}

// A single in-flight refresh shared across every request that 401s at once, so
// N simultaneous failures trigger exactly ONE token exchange, not N.
let _refreshPromise = null;

// Exchanges the refresh token for a new access token. Updates `user-info` in
// place (new access token + rotated refresh token) and resolves with the new
// access token. Rejects if there is no refresh token or the exchange fails.
export function refreshAccessToken() {
  if (_refreshPromise) return _refreshPromise;

  const refresh = getRefreshToken();
  if (!refresh) {
    return Promise.reject(new Error('no_refresh_token'));
  }

  _refreshPromise = (async () => {
    // Use fetch (not the app axios instance) so this call bypasses the axios
    // interceptors entirely — it can never itself trigger a refresh loop.
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: OAUTH_CLIENT_ID,
      refresh_token: refresh,
    });
    const res = await fetch(API_URL + '/oauth/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });
    if (!res.ok) {
      throw new Error('refresh_failed');
    }
    const data = await res.json();
    if (!data.access_token) {
      throw new Error('refresh_failed');
    }
    const info = getUserInfo() || {};
    info.access_token = data.access_token;
    if (data.refresh_token) info.refresh_token = data.refresh_token;
    localStorage.setItem('user-info', JSON.stringify(info));
    return data.access_token;
  })();

  // Clear the shared promise once settled so a later expiry can refresh again.
  _refreshPromise.finally(() => { _refreshPromise = null; });
  return _refreshPromise;
}

// ── Subscription-plan status ────────────────────────────────────────────────
// Reads the plan_status/days_until_expiry fields the backend attaches to
// /profile (see AuthCallback.vue, Account.vue) — never computed client-side,
// since expiry enforcement is entirely backend-driven.
export function getPlanStatus() {
  const info = getUserInfo();
  return (info && info.plan_status) || null;
}

export function getDaysUntilExpiry() {
  const info = getUserInfo();
  return info && typeof info.days_until_expiry === "number" ? info.days_until_expiry : null;
}

export function isPlanExpired() {
  return getPlanStatus() === "EXPIRED";
}

export function isPlanExpiringSoon() {
  return getPlanStatus() === "EXPIRING_SOON";
}
